import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import { Container } from '../../components/ui/Container'
import BookFilter from '../../components/features/books/BookFilter'
import { BookSearch } from '../../components/features/books/BookSearch'
import { ShopHero } from '../../components/features/shop/ShopHero'
import { BookContent } from '../../components/features/shop/BookContent'
import PartnershipBanner from '../../components/features/shop/PartnershipCard'
import { getProducts, getAllCategories } from '../../lib/data/products'

const ITEMS_PER_PAGE = 12

interface ShopSearchParams {
  page?: number
  search?: string
  category?: string
  sort?: string
  minPrice?: number
  maxPrice?: number
}

export const Route = createFileRoute('/shop/')({
  component: Shop,
  loader: async () => {
    // Use static data for categories
    const categories = getAllCategories()
    return { categories }
  },
})

function Shop() {
  const { categories } = Route.useLoaderData()
  const navigate = useNavigate({ from: '/shop' })
  const searchParams = useSearch({ from: '/shop/' }) as ShopSearchParams

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  // Get current params
  const currentPage = Number(searchParams.page) || 1
  const currentSearch = (searchParams.search as string) || ''
  const currentCategory = (searchParams.category as string) || ''

  // Get products from static data - sorted by newest
  const { products, total, totalPages } = useMemo(() => {
    return getProducts({
      page: currentPage,
      limit: ITEMS_PER_PAGE,
      search: currentSearch,
      category: currentCategory,
      sortBy: 'newest',
    })
  }, [currentPage, currentSearch, currentCategory])

  // Derive filters object from URL params
  const filters = useMemo(() => ({
    search: currentSearch,
    category: currentCategory,
    minPrice: 0,
    maxPrice: 500000,
    sortBy: 'newest',
  }), [currentSearch, currentCategory])

  const handleSearch = (value: string) => {
    navigate({
      search: (prev) => ({ ...prev, search: value, page: 1 }),
    })
  }

  const handleFiltersChange = (newFilters: any) => {
    navigate({
      search: (prev) => ({
        ...prev,
        category: newFilters.category || '',
        page: 1,
      }),
    })
  }

  const handlePageChange = (page: number) => {
    navigate({
      search: (prev) => ({ ...prev, page }),
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleResetError = () => {
    // No error state with static data
  }

  return (
    <div className="min-h-screen bg-brand-slate-50">
      <ShopHero />

      <Container className="py-12">
        {/* Search Bar */}
        <div className="mb-8 -mt-8 relative z-10">
          <BookSearch
            onSearch={handleSearch}
            defaultValue={currentSearch}
            className="max-w-2xl mx-auto"
          />
        </div>

        {/* Filters and Results */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <aside className="w-full lg:w-80 shrink-0">
            <BookFilter
              categories={categories}
              priceRange={{ min: 0, max: 500000 }}
              onFiltersChange={handleFiltersChange}
              initialFilters={filters}
            />
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            <BookContent
              books={products}
              total={total}
              loading={false}
              error={null}
              viewMode={viewMode}
              currentPage={currentPage}
              totalPages={totalPages}
              searchTerm={currentSearch}
              category={currentCategory}
              onViewModeChange={setViewMode}
              onPageChange={handlePageChange}
              onResetError={handleResetError}
            />
          </main>
        </div>

        {/* Partnership Banner */}
        <div className="mt-32">
          <PartnershipBanner />
        </div>
      </Container>
    </div>
  )
}
